import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// ═══════════════════════════════════════════════════════════════════════
// TOAST PROVIDER — drop-in replacement for native alert()/confirm().
// useToast() gives you:
//   showToast(message, type)          type: 'success' | 'error' | 'info'
//   showConfirm(message, opts)        opts: { title, confirmLabel, confirmColor }
//                                      returns a Promise<boolean>
// ═══════════════════════════════════════════════════════════════════════
const ToastCtx = createContext(null);

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

const ICONS = {
  success: { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', glyph: '✓' },
  error:   { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', glyph: '✕' },
  info:    { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', glyph: 'i' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const idRef = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) setTimeout(() => dismissToast(id), duration);
    return id;
  }, [dismissToast]);

  const showConfirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        message,
        title: opts.title || 'Please Confirm',
        confirmLabel: opts.confirmLabel || 'Confirm',
        confirmColor: opts.confirmColor || '#ef4444',
        resolve,
      });
    });
  }, []);

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  return (
    <ToastCtx.Provider value={{ showToast, dismissToast, showConfirm }}>
      {children}
      {ReactDOM.createPortal(
        <>
          <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 100000, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}>
            {toasts.map(t => {
              const meta = ICONS[t.type] || ICONS.info;
              return (
                <div key={t.id} role="status" style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                  borderRadius: 12, background: '#fff', border: `1px solid ${meta.border}`,
                  boxShadow: '0 12px 32px rgba(15,31,69,0.16)', animation: 'cx-toast-in 0.2s ease-out',
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0, background: meta.bg,
                    color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>{meta.glyph}</span>
                  <span style={{ fontSize: 13, color: '#0f1f45', lineHeight: 1.5, flex: 1 }}>{t.message}</span>
                  <button onClick={() => dismissToast(t.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8',
                    fontSize: 14, lineHeight: 1, padding: 2, flexShrink: 0,
                  }}>✕</button>
                </div>
              );
            })}
          </div>
          <style>{`@keyframes cx-toast-in { from { opacity:0; transform:translateX(16px); } to { opacity:1; transform:translateX(0); } }`}</style>

          {confirmState && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100001, padding: 16 }}>
              <div style={{ background: '#fff', borderRadius: 16, width: 'min(92vw,420px)', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f1f45', margin: '0 0 8px' }}>{confirmState.title}</h3>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.6 }}>{confirmState.message}</p>
                </div>
                <div style={{ display: 'flex', gap: 10, padding: '16px 24px' }}>
                  <button onClick={() => closeConfirm(false)} style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#4a5568', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={() => closeConfirm(true)} style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: 'none', background: confirmState.confirmColor, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{confirmState.confirmLabel}</button>
                </div>
              </div>
            </div>
          )}
        </>,
        document.body
      )}
    </ToastCtx.Provider>
  );
}
